import React, { useState, useEffect } from 'react';
import { Users, Search, RotateCcw, Archive } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import AdminLayout from '../../components/admin/AdminLayout';
import { adminApi } from '../../lib/api';

import { toast } from 'sonner';

const ArchivedCustomers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get(`/admin/customers`, { params: { search: searchQuery, limit: 100, show_archived: true } });
      // Archived = deleted_at set (the API never sends an `archived` field on accounts); the
      // rebuilt page's isArchivedAccount rule.
      const archivedOnly = response.data.customers.filter(c => Boolean(c.deleted_at || c.archived === true));
      setCustomers(archivedOnly);
    } catch (err) {
      console.error('Error fetching archived customers:', err);
      toast.error('Failed to load archived customers');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (userId) => {
    if (!confirm('Restore this customer?')) return;
    
    try {
      await adminApi.patch(
        `/admin/customers/${userId}/archive?archived=false`,
        {}
      );
      toast.success('Customer restored successfully!');
      fetchCustomers();
    } catch (err) {
      console.error('Error restoring customer:', err);
      toast.error('Failed to restore customer');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-consumer border-t-transparent"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* V5 Page Header */}
      <div className="mb-8">
        <p className="font-mono text-consumer text-sm mb-1">// archived_customers</p>
        <h1 className="font-display text-3xl text-navy">Archived Customers</h1>
        <p className="text-navy/50 mt-1">
          Manage archived customer accounts • {customers.length} archived
        </p>
      </div>

      {/* V5 Search Card */}
      <div className="bg-white rounded-2xl p-5 mb-6 border border-navy/5 shadow-sm" style={{ borderLeft: '4px solid #f97316' }}>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-navy/30" />
            <Input
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && fetchCustomers()}
              className="pl-11 h-12 border-2 border-navy/10 focus:border-consumer rounded-xl"
            />
          </div>
          <Button 
            onClick={fetchCustomers}
            className="h-12 px-6"
            style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)', color: 'white' }}
          >
            <Search size={18} className="mr-2" />
            Search
          </Button>
        </div>
      </div>

      {/* V5 Results Card */}
      <div className="bg-white rounded-2xl border border-navy/5 shadow-sm overflow-hidden">
        {customers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-6">
              <Archive className="w-10 h-10 text-navy/20" />
            </div>
            <h3 className="font-display text-xl text-navy mb-2">No Archived Customers</h3>
            <p className="text-navy/50">
              {searchQuery ? 'No archived customers match your search' : 'No customers have been archived'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-navy/5 bg-cream/50">
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Joined</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Claims</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-navy/50 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy/5">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-navy to-navy/70 flex items-center justify-center text-white font-semibold">
                          {(customer.first_name?.[0] || customer.email?.[0] || '?').toUpperCase()}
                        </div>
                        <div>
                          <p className="font-semibold text-navy">
                            {customer.first_name || customer.last_name 
                              ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim()
                              : 'Unnamed User'
                            }
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-navy/70">{customer.email}</td>
                    <td className="px-6 py-4 text-navy/70">{formatDate(customer.created_at)}</td>
                    <td className="px-6 py-4">
                      <span className="px-2.5 py-1 bg-consumer/10 text-consumer text-sm font-medium rounded-full">
                        {customer.total_claims || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <Button
                        onClick={() => handleRestore(customer.id)}
                        size="sm"
                        className="rounded-xl"
                        style={{ background: 'linear-gradient(135deg, #22c55e, #16a34a)', color: 'white' }}
                      >
                        <RotateCcw size={14} className="mr-1.5" />
                        Restore
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ArchivedCustomers;
